import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { createEngine2d, Engine2d } from '@ff-os/rendering-react/createEngine2d';
import { Clip } from '@ff-os/rendering-shared/Clip';
import { Spaces } from '@ff-os/rendering-shared/coordinates';
import type { PhotoPrintsPdpDispatch } from '@ff-scene-plugins/photo-prints-redux/store';
import useIntersectionObserver from '@react-hook/intersection-observer';
import classNames from 'classnames';
import { Paintable } from 'ff-canvas';
import { useContain } from '../../hooks/useContain';
import { useDebounce } from '../../hooks/useDebounce';
import { useSize } from '../../hooks/useSize';
import styles from './PaintablesRenderer.module.scss';

export const PaintablesRenderer = ({
    paintables, spaces, fontsReady, canvasClassName,
    language, previewClip, withTransitions = false
}: Props) => {

    const { t } = useTranslation();

    const dispatch = useDispatch<PhotoPrintsPdpDispatch>();

    const containerRef = useRef<HTMLDivElement>(null);
    const containerSize = useSize(containerRef);

    const sceneRatio = spaces.sceneWidth / spaces.sceneHeight;
    const contentSize = useContain(containerSize, sceneRatio);

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const paintablesRef = useRef<Paintable[]>([]);
    const [engine2d, setEngine2d] = useState<Engine2d | null>(null);
    const { isIntersecting: shouldRender } = useIntersectionObserver(containerRef);

    const previousPaintables = useRef<Paintable[]>([]);

    // 2D engine creation:

    const handleEngine2dCreation = useCallback(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server' && contentSize && shouldRender) {
            if (!engine2d?.is(contentSize, spaces)) {
                engine2d?.dispose();
                setEngine2d(createEngine2d(
                    paintablesRef,
                    canvasRef.current!,
                    contentSize,
                    spaces,
                    { t, language },
                    { previewClip },
                    dispatch
                ));
            }
        }
    }, [contentSize, engine2d, spaces, t, language, previewClip, shouldRender, dispatch]);

    // Debounce the engine creation by 50ms:
    const debouncedHandleEngine2dCreation = useDebounce(handleEngine2dCreation, 50);

    // 2D engine creation effect:

    useEffect(() => {
        debouncedHandleEngine2dCreation();
    }, [debouncedHandleEngine2dCreation]);

    // 2D engine paintables rerender:

    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server') {

            const paintablesChanged = JSON.stringify(previousPaintables.current) !== JSON.stringify(paintables);

            if (contentSize && shouldRender && engine2d?.is(contentSize, spaces) && paintablesChanged) {
                paintablesRef.current = paintables;
                previousPaintables.current = paintables;
                engine2d.requestRender();
            }
        }
    }, [contentSize, engine2d, paintables, spaces, shouldRender]);

    // Rerender `text` and `photo` objects once fonts have loaded:

    useEffect(() => {
        if (process.env.REACT_APP_TO_BUILD !== 'website-server' && engine2d && fontsReady) {
            engine2d.disposeByIds(findIdsAffectedByFontLoadIn(paintablesRef.current));
            engine2d.requestRender();
        }
    }, [engine2d, fontsReady]);

    const contentClassNames = classNames({ [styles.transitions]: withTransitions });
    const canvasClassNames = classNames(canvasClassName, styles.canvas);

    return (
        <div className={styles.container} ref={containerRef}>
            <div className={styles.center}>
                <div className={contentClassNames} style={contentSize}>
                    <canvas
                        key={contentSize?.width + "x" + contentSize?.height}
                        ref={canvasRef} className={canvasClassNames} />
                </div>
            </div>
        </div>
    );
};

type Props = {
    paintables: Paintable[];
    spaces: Spaces;
    language: string;
    fontsReady: boolean;
    previewClip?: Clip;
    withTransitions?: boolean;
    canvasClassName?: string;
};

const findIdsAffectedByFontLoadIn = (
    paintables: Paintable[]
): string[] => {
    return paintables
        .filter(p => (p.type === 'photo' && !p.url) || p.type === 'text')
        .map(p => p.id);
};